import { Button, Descriptions, Empty, Input, Popconfirm, Space, Timeline, Typography, Upload } from '@arco-design/web-react';
import { IconDelete } from '@arco-design/web-react/icon';
import BccTabs, { BccTabPane } from '../BccTabs';
import PillTag, { type PillColor } from '../PillTag';
import StatusTag from '../StatusTag';
import { fmtTime } from '../../utils/format';
import { workflowInstanceStateMeta } from '../../utils/labels';
import type { Ticket, TicketAttachment } from '../../api/ticket';
import type { WorkflowInstance } from '../../api/workflow';

/** 审核流程实例状态 Tag：文案/语义色单源 utils/labels.ts（与 WorkflowTimeline 同屏同词同色） */
const wfStateTag = (state?: string) => {
  const [label, color] = workflowInstanceStateMeta(state);
  return <PillTag color={color as PillColor}>{label}</PillTag>;
};

export interface TicketDetailTabsProps {
  /** 工单详情（沟通/附件/关联三段的数据源） */
  detail: Ticket;
  /** 审核流程实例（「关联」页签展示实例号与状态） */
  wfInst: WorkflowInstance | null;
  comment: string;
  commentSaving: boolean;
  onCommentChange: (v: string) => void;
  onAddComment: () => void;
  attachments: TicketAttachment[];
  /** 上传附件（beforeUpload 钩子；入口侧负责阻止默认上传并刷新列表） */
  onUpload: (file: File) => Promise<unknown>;
  onDeleteAttachment: (att: TicketAttachment) => void;
  onPreview: (att: TicketAttachment) => void;
  /** 关联通话 CDR → 右侧再叠一层呼叫记录抽屉 */
  onOpenCdr: (cdrId: number) => void;
  /** 回访外呼任务 → 跳外呼页 */
  onOpenOutbound: (taskId: number) => void;
  /** 审核流程实例 → 跳待办中心 */
  onOpenWorkflow: () => void;
  /** 打开「关联通话/会话」弹窗 */
  onOpenLink: () => void;
}

/**
 * 工单详情页签区（沟通 / 附件 / 关联）：
 * 三段内容随详情数据渲染，写操作与跳转由入口侧显式传入（本分片不持有状态、不直连 store）。
 */
export default function TicketDetailTabs({
  detail,
  wfInst,
  comment,
  commentSaving,
  onCommentChange,
  onAddComment,
  attachments,
  onUpload,
  onDeleteAttachment,
  onPreview,
  onOpenCdr,
  onOpenOutbound,
  onOpenWorkflow,
  onOpenLink,
}: TicketDetailTabsProps) {
  // 评论：详情内嵌 comments 数组
  const comments = Array.isArray(detail.comments) ? detail.comments : [];
  return (
    <BccTabs contentPadding={false} defaultActiveTab="comments">
      <BccTabPane key="comments" title={`沟通（${comments.length}）`}>
        {comments.length === 0 ? (
          <Empty description="暂无评论" />
        ) : (
          <Timeline>
            {comments.map((c, i) => (
              <Timeline.Item key={c.id ?? i}>
                <Typography.Text bold>{c.author_name || '系统'}</Typography.Text>
                <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                  {fmtTime(c.created_at)}
                </Typography.Text>
                <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{c.content}</div>
              </Timeline.Item>
            ))}
          </Timeline>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Input style={{ flex: 1 }} placeholder="添加评论" value={comment} onChange={onCommentChange} onPressEnter={onAddComment} />
          <Button type="primary" loading={commentSaving} onClick={onAddComment}>
            发送
          </Button>
        </div>
      </BccTabPane>
      <BccTabPane key="attachments" title={`附件（${attachments.length}）`}>
        <Upload
          drag
          multiple={false}
          disabled={!detail}
          tip="上传图片/文档/录音等（≤10MB）"
          beforeUpload={(file) => onUpload(file)}
        />
        <Space direction="vertical" style={{ width: '100%', marginTop: 12 }}>
          {attachments.length === 0 && <Empty description="暂无附件" />}
          {attachments.map((att) => (
            <div key={att.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography.Text style={{ fontSize: 13 }}>{att.name}</Typography.Text>
              <Space size={8}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {(att.size / 1024).toFixed(0)}KB
                </Typography.Text>
                <Button size="mini" onClick={() => onPreview(att)}>
                  预览
                </Button>
                <Popconfirm title="删除该附件？删除后不可恢复（对象存储中的文件同时移除）。" onOk={() => onDeleteAttachment(att)}>
                  <Button size="mini" status="danger" icon={<IconDelete />} />
                </Popconfirm>
              </Space>
            </div>
          ))}
        </Space>
      </BccTabPane>
      <BccTabPane key="links" title="关联">
        <Descriptions
          column={1}
          size="small"
          data={[
            {
              label: '关联通话 CDR',
              value: detail.cdr_id ? (
                <Typography.Text
                  style={{ cursor: 'pointer', color: 'rgb(var(--link-6))' }}
                  onClick={() => onOpenCdr(detail.cdr_id as number)}
                >
                  #{detail.cdr_id} 查看呼叫记录
                </Typography.Text>
              ) : (
                '-'
              ),
            },
            { label: '关联在线会话', value: detail.chat_session_id ? `#${detail.chat_session_id}` : '-' },
            {
              label: '回访外呼任务',
              value: detail.callback_task_id ? (
                <Space size={6}>
                  <Typography.Text
                    style={{ cursor: 'pointer', color: 'rgb(var(--link-6))' }}
                    onClick={() => onOpenOutbound(detail.callback_task_id as number)}
                  >
                    #{detail.callback_task_id} 查看外呼任务
                  </Typography.Text>
                  {detail.callback_task && (
                    <Space size={6}>
                      <StatusTag value={detail.callback_task.status} />
                      {detail.callback_task.status !== 'draft' && (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          进度 {detail.callback_task.called}/{detail.callback_task.total}，接通 {detail.callback_task.connected}
                        </Typography.Text>
                      )}
                    </Space>
                  )}
                </Space>
              ) : detail.follow_up ? (
                '工单未关闭，关闭后自动生成'
              ) : (
                '-'
              ),
            },
            {
              label: '审核流程实例',
              value: wfInst ? (
                <Space size={6}>
                  <span className="bcc-mono">#{wfInst.id}</span>
                  {wfStateTag(wfInst.state)}
                  <Typography.Text
                    style={{ cursor: 'pointer', color: 'rgb(var(--link-6))', fontSize: 12 }}
                    onClick={onOpenWorkflow}
                  >
                    去待办中心
                  </Typography.Text>
                </Space>
              ) : detail.workflow_instance_id ? (
                `#${detail.workflow_instance_id}`
              ) : (
                '-'
              ),
            },
          ]}
        />
        <Button size="small" style={{ marginTop: 8 }} onClick={onOpenLink}>
          关联通话/会话
        </Button>
      </BccTabPane>
    </BccTabs>
  );
}
